import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';

import { EnvironmentService } from '@ecm/consumer';
import { EcmDispatcherModule, EnvUtils, OrangeAppInfoService } from '@ecm/dispatcher';
import { Brand } from '@ecm/portfolio';

import { OriginCode } from '../business/kenobi/origin-code.enum';
import { TrackingUtils } from '../utils/tracking.utils';

/**
 * Resolves the `codeIdor` Kenobi param : the origin application of the user journey 🚪
 *
 * Priority is evaluated on each read :
 *  1. native app info (`orange-app-info` local storage entry) : locked for the whole session once found
 *  2. `src` query param, updated on each navigation
 *  3. environment (legacy)
 *  4. default `HP`
 *
 * In webview, a NDE native app basicat code received from 1. or 2. is transcoded (`OSZ` → `APORANGE`, `SSZ` → `APSOSH`)
 * and gives the brand of the native app hosting the webview (see `ndeAppBrand`)
 */
@Injectable({
    providedIn: 'root',
})
export class CodeIdorResolver {
    /**
     * NDE native apps basicat codes, mapped to their brand
     * Historical native apps (`OEM`, `MYH`) are not part of it : their code is kept as is
     * @type {Partial<Record<keyof typeof OriginCode, Brand>>}
     */
    private static readonly NDE_APP_BRANDS: Readonly<Partial<Record<keyof typeof OriginCode, Brand>>> = {
        OSZ: Brand.ORANGE,
        SSZ: Brand.SOSH,
    };

    private readonly appInfoService = inject(OrangeAppInfoService);
    private readonly environmentService = inject(EnvironmentService);
    private readonly router = inject(Router);

    /**
     * Class name for console logs
     * @type {string}
     */
    private readonly className: string = 'CodeIdorResolver';

    /**
     * `codeIdor` from native app info, locked for the whole session once resolved
     * @type {string}
     */
    private lockedCodeIdor: string | null = null;

    /**
     * Last `src` query param value seen on navigation
     * @type {string}
     */
    private queryParamCodeIdor: string | null = null;

    /**
     * Whether a code is an `OriginCode` key (own keys only, inherited ones like `toString` are excluded)
     * @param {string} code
     * @returns {boolean}
     */
    private static isOriginCodeKey(code: string): code is keyof typeof OriginCode {
        return Object.hasOwn(OriginCode, code);
    }

    /**
     * Transcode a code with `OriginCode`
     * @param {string} code
     * @returns {string} transcoded value, or the code as is when unknown
     */
    private static transcode(code: string): string {
        return CodeIdorResolver.isOriginCodeKey(code) ? OriginCode[code] : code;
    }

    public constructor() {
        // The queryParams of activatedRoute works only for the route used for constructor
        // We have to watch router navigation event to get it from root route
        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                map(() => TrackingUtils.getSrcQueryParam(this.router.routerState.root.snapshot.queryParams)),
                filter((srcValue) => srcValue !== undefined),
            )
            .subscribe({ next: (srcValue) => (this.queryParamCodeIdor = srcValue) });
    }

    /**
     * `codeIdor` for the current session, transcoded when received from a NDE native app hosting the webview
     * @returns {string}
     */
    public get codeIdor(): string {
        const sourceCodeIdor: string = this.resolveSourceCodeIdor();

        return this.getNdeAppBrand(sourceCodeIdor) ? CodeIdorResolver.transcode(sourceCodeIdor) : sourceCodeIdor;
    }

    /**
     * Brand of the NDE native app hosting the webview
     * @returns {Brand} `null` outside webview, or when the origin is not a NDE native app
     */
    public get ndeAppBrand(): Brand | null {
        return this.getNdeAppBrand(this.resolveSourceCodeIdor());
    }

    /**
     * Resolve the `codeIdor` as received, following the sources priority
     * @returns {string}
     */
    private resolveSourceCodeIdor(): string {
        if (this.lockedCodeIdor) {
            return this.lockedCodeIdor;
        }

        const appInfoCodeIdor = this.appInfoService.appName;
        if (appInfoCodeIdor) {
            this.lockedCodeIdor = appInfoCodeIdor;
            EcmDispatcherModule.sendConsoleInfo(this.className, `codeIdor from native app info: ${appInfoCodeIdor}`);
            return appInfoCodeIdor;
        }

        const queryParamCodeIdor = this.getCodeIdorFromQueryParam();
        if (queryParamCodeIdor) {
            return queryParamCodeIdor;
        }

        // eslint-disable-next-line deprecation/deprecation -- legacy fallback, kept on purpose
        return this.getCodeIdorFromEnvironment();
    }

    /**
     * Get the brand of a NDE native app, only in webview
     * @param {string} sourceCodeIdor `codeIdor` as received
     * @returns {Brand} `null` outside webview, or when the code is not a NDE native app one
     */
    private getNdeAppBrand(sourceCodeIdor: string): Brand | null {
        if (!EnvUtils.isWebview() || !CodeIdorResolver.isOriginCodeKey(sourceCodeIdor)) {
            return null;
        }

        return CodeIdorResolver.NDE_APP_BRANDS[sourceCodeIdor] ?? null;
    }

    /**
     * Get `codeIdor` from the `src` query param, either the one seen on navigation or the current one
     * @returns {string} `null` when absent
     */
    private getCodeIdorFromQueryParam(): string | null {
        return (
            this.queryParamCodeIdor ||
            TrackingUtils.getSrcQueryParam(this.router.routerState.root.snapshot.queryParams) ||
            null
        );
    }

    /**
     * LEGACY - Get `codeIdor` from environment (`env.ORIGIN`, or `env.BRAND` for webview app)
     * Kept as a fallback for apps still deployed with those keys
     * @returns {string} `HP` when no environment origin is defined
     * @deprecated superseded by the native app info and the `src` query param
     */
    private getCodeIdorFromEnvironment(): string {
        const env = this.environmentService.instant();

        if (env?.ORIGIN) {
            return CodeIdorResolver.transcode(env.ORIGIN.toUpperCase());
        }

        if (EnvUtils.isWebview() && env?.BRAND) {
            return env.BRAND.toUpperCase() === Brand.SOSH ? OriginCode.MYSSOSH : OriginCode.OEM;
        }

        return OriginCode.HP;
    }
}
